import { useTheme, styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SmallForm from './small-form';
import { useSnackbarContext } from '../base/snackbar';

import Arranger from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import LogoSelector from '~c/components/base/icon/logo-selector';
import { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import { nodeAuthApi } from '~c/services';

interface Props {
  className?: string;
}

const ConfirmResetPasswordImpl: React.FC<Props> = ({ className }) => {
  const navigate = useNavigate();
  const { code } = useParams();
  const theme = useTheme();
  const prefersDarkMode = theme.palette.mode === 'dark';
  const Snackbar = useSnackbarContext();
  const [confirmReset, { isLoading }] =
    nodeAuthApi.useConfirmPasswordResetMutation();
  const [isLinkValid, setIsLinkValid] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    const checkLinkValidity = async () => {
      try {
        const response = await fetch(`/auth/confirm-reset-password/${code}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          setIsLinkValid(false);
          setTimeout(() => navigate('/sign-in'), 5000);
        } else {
          setIsLinkValid(true);
        }
      } catch (error) {
        setIsLinkValid(false);
        Snackbar.open('Failed to verify reset link', { showOk: true });
      }
    };

    checkLinkValidity();
  }, [code, navigate, Snackbar]);

  const handleConfirm = async () => {
    try {
      await confirmReset({
        resetPasswordId: code ?? '',
        newPassword: '',
      });
      Snackbar.open('Password has been reset successfully.', { showOk: true });
      navigate('/sign-in');
    } catch (error: any) {
      Snackbar.open(error?.data?.error ?? 'Failed to reset password', {
        showOk: true,
      });
    }
  };

  const renderTitle = () => (
    <Arranger direction="column" alignItems="center">
      <div className="logo-container">
        <LogoSelector prefersDarkMode={prefersDarkMode} />
      </div>
      <Text sz="h1">Confirm Password Reset</Text>
    </Arranger>
  );

  if (isLinkValid === null) {
    return (
      <SmallForm className={className} title={renderTitle()}>
        <Arranger direction="column" alignItems="center">
          <Text>Checking reset link validity...</Text>
        </Arranger>
      </SmallForm>
    );
  }

  if (!isLinkValid) {
    return (
      <SmallForm className={className} title={renderTitle()}>
        <Arranger direction="column" alignItems="center">
          <Text>
            This password reset link is expired or has already been used.
          </Text>
        </Arranger>
      </SmallForm>
    );
  }

  return (
    <SmallForm className={className} title={renderTitle()}>
      <Arranger direction="column" alignItems="center">
        <Text>Please confirm that you want to reset your password.</Text>
        <div style={{ height: '40px' }} />
        <Button
          loading={isLoading}
          onClick={handleConfirm}
          disabled={!isLinkValid}
        >
          Confirm Reset
        </Button>
      </Arranger>
    </SmallForm>
  );
};

export const ConfirmResetPassword = muiStyled(ConfirmResetPasswordImpl)(
  ({ theme }) => css`
    .logo-container {
      svg {
        width: 150px;
        height: 50px;
      }
    }

    h1 {
      padding-top: ${spc.xxxs};
    }

    p {
      margin-bottom: ${spc.m};
    }

    button {
      margin-bottom: ${spc.between_vertical_s};
    }
  `
);

export default ConfirmResetPassword;
